import { useEffect, useSyncExternalStore } from "react";

import { DISPLAY_PREFERENCES_CHANGED_EVENT } from "@/hooks/useUserNotificationTimezone";
import { fetchNotificationTimezone } from "@/services/notificationsApi";
import { getDisplayTimezoneMode } from "@/utils/displayTimezoneMode";
import { deviceIanaTimeZone, resolveUserReportingTimeZone } from "@/utils/reportingTimeZone";

/**
 * The trader's reporting time zone for the Lab, kept outside React.
 *
 * `useUserNotificationTimezone` reads Clerk's `useAuth`, which re-renders its caller on every
 * session-token refresh (about once a minute); in the Lab that re-rendered the whole page and
 * froze it for a moment. The Lab is behind sign-in, so it fetches the zone once per app load
 * (and again when Display Preferences change) and only re-renders when the zone changes.
 */
let zone: string | null = null;
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();

function set(next: string | null): void {
  if (next === zone) return;
  zone = next;
  listeners.forEach((l) => l());
}

function load(force = false): void {
  if (loading || (zone != null && !force)) return;
  loading = fetchNotificationTimezone()
    .then((res) => set(resolveUserReportingTimeZone(res.timezone, getDisplayTimezoneMode(res, deviceIanaTimeZone()))))
    .catch(() => set(zone ?? deviceIanaTimeZone()))
    .finally(() => {
      loading = null;
    });
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useStrategyRdTimezone(): string | null {
  useEffect(() => {
    load();
    const onPrefs = (ev: Event) => {
      if ((ev as CustomEvent<{ setting?: string }>).detail?.setting === "timezone") load(true);
    };
    window.addEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onPrefs as EventListener);
    return () => window.removeEventListener(DISPLAY_PREFERENCES_CHANGED_EVENT, onPrefs as EventListener);
  }, []);
  return useSyncExternalStore(subscribe, () => zone);
}
